import React, { useEffect, useState, useRef } from 'react';
import { ZoomMtg } from '@zoom/meetingsdk';

ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();

function ZoomMeeting() {
    const leaveUrl = '/meetingEnd'
    const [bgStatus, setBgStatus] = useState({
        isApplied: false,
        message: 'Initializing...',
        currentBG: null
    });

    // Store formatted backgrounds for reference
    const [formattedBGs, setFormattedBGs] = useState([]);
    const [initData, setInitData] = useState({});
    const [isFromMobileApp, setIsFromMobileApp] = useState(false)
    const lastKnownBGRef = useRef(null);

    const getBackgroundCollection = (virtualBGs) => {
        if (!virtualBGs) return [];
        if (Array.isArray(virtualBGs)) return virtualBGs;
        if (Array.isArray(virtualBGs?.virtualBg)) return virtualBGs.virtualBg;
        if (Array.isArray(virtualBGs?.backgrounds)) return virtualBGs.backgrounds;
        if (Array.isArray(virtualBGs?.virtualBackgrounds)) return virtualBGs.virtualBackgrounds;
        return [];
    };

    const getBackgroundId = (bg, fallbackIndex = 0) => (bg?._id || bg?.id || bg?.bgId || bg?.backgroundId || `vb_${fallbackIndex}`);

    const getBackgroundUrl = (bg) => (bg?.imageUrl || bg?.url || bg?.backgroundUrl || bg?.fileUrl || '');

    const formatVirtualBackgrounds = (virtualBGs) => {
        const sourceBackgrounds = getBackgroundCollection(virtualBGs);
        return sourceBackgrounds
            .map((bg, ind) => {
                const url = getBackgroundUrl(bg);
                if (!url) return null;

                return {
                    displayName: bg?.displayName || `Background ${ind + 1}`,
                    fileName: `bg_${ind}_${Date.now()}.jpg`,
                    id: getBackgroundId(bg, ind),
                    url,
                    isDefault: !!bg?.isDefault
                };
            })
            .filter(Boolean);
    };

    const controlVideoState = (shouldStart) => {
        const actionName = shouldStart ? 'startVideo' : 'stopVideo';
        const fallbackMessage = shouldStart ? "Video started with mute fallback" : "Video stopped with mute fallback";

        return new Promise((resolve) => {
            if (typeof ZoomMtg[actionName] === 'function') {
                ZoomMtg[actionName]({
                    success: function () {
                        resolve();
                    },
                    error: function (error) {
                        console.error(`❌ Error calling ${actionName}:`, error);
                        resolve();
                    }
                });
                return;
            }

            // Fallback for older SDK behavior to avoid breaking existing flow.
            ZoomMtg.mute({
                mute: !shouldStart,
                success: function () {
                    resolve();
                },
                error: function (error) {
                    console.error("❌ Error with mute fallback:", error);
                }
            });
        });
    };

    useEffect(() => {
        const receiveMessage = (event) => {
            const fromMobile = event?.data?.isFromMobileApp ? true : false;
            setIsFromMobileApp(fromMobile)
            if ((event?.data && typeof event?.data === "string") || (fromMobile && typeof event?.data === "object")) {
                try {
                    const data = fromMobile ? event.data : JSON.parse(event.data);
                    if (data?.eventType === "growkarma_zoomMeetingInit") {
                        setInitData(data);
                        console.log("------------------------ Received Zoom Init Data ------------------------", data);
                    }
                } catch (error) {
                    console.error("Error parsing message data:", error);
                }
            }
        };
        window.addEventListener('message', receiveMessage);

        return () => {
            window.removeEventListener('message', receiveMessage);
        };
    }, []);

    useEffect(() => {
        if (initData?.signature && initData?.meetingNumber) {
            const { signature, sdkKey, meetingNumber, passWord, userName, userEmail, tk, zak, virtualBGs = [], } = initData;
            const sourceBackgrounds = getBackgroundCollection(virtualBGs);
            const formattedBGs = formatVirtualBackgrounds(virtualBGs);

            setFormattedBGs(formattedBGs);

            startMeeting(signature, sdkKey, meetingNumber, passWord, userName, userEmail, tk, zak, formattedBGs, sourceBackgrounds);
        }
    }, [initData]);

    function startMeeting(signature, sdkKey, meetingNumber, passWord, userName, userEmail, tk, zak, formattedBGs, originalBGs) {
        console.log("--------------- Starting Meeting Init Part ---------------");
        setBgStatus({ isApplied: false, message: 'Joining meeting...', currentBG: null });

        const zmmtgRoot = document.getElementById('zmmtg-root');
        if (zmmtgRoot) {
            zmmtgRoot.style.display = 'block';
        }

        ZoomMtg.init({
            leaveUrl: leaveUrl,
            patchJsMedia: true,
            // Disable video by default to apply background first
            disablePreview: false,
            videoHeader: false,
            success: (success) => {
                console.log("--------------- Meeting Init Success ---------------");

                ZoomMtg.join({
                    signature: signature,
                    sdkKey: sdkKey,
                    meetingNumber: meetingNumber,
                    passWord: passWord,
                    userName: userName,
                    userEmail: userEmail,
                    tk: tk,
                    zak: zak,
                    success: async (success) => {
                        console.log("--------------- Meeting Join Success ---------------");
                        setBgStatus({ isApplied: false, message: 'Meeting joined, preparing backgrounds...', currentBG: null });

                        try {
                            const eventData = JSON.stringify({ eventType: "growkarma_zoomMeetingStart" });
                            window.top.postMessage(eventData, '*');
                        } catch (error) {
                            console.log("Error posting meeting start message:", error);
                        }

                        // Ensure video is OFF before applying background.
                        await controlVideoState(false);

                        if (formattedBGs && formattedBGs.length > 0) {
                            const defaultBG = originalBGs?.find(bg => bg?.isDefault === true);
                            try {
                                await applyVirtualBackgrounds(formattedBGs, defaultBG);
                            } catch (error) {
                                console.error("Error applying virtual backgrounds:", error);
                            }

                        } else {
                            setBgStatus({ isApplied: false, message: 'No virtual backgrounds provided', currentBG: null });
                        }
                    },
                    error: (error) => {
                        console.error("--------------- Error Joining Meeting ---------------", error);
                        setBgStatus({ isApplied: false, message: `Error joining: ${error.reason || 'Unknown error'}`, currentBG: null });
                    }
                });

                ZoomMtg.inMeetingServiceListener('onUserJoin', function (data) {
                    try {
                        const eventData = data ? JSON.stringify({ ...data, eventType: "growkarma_zoomOnUserJoin" }) : "{}";
                        console.log("--------------- User Has Been Joined ---------------");
                        window.top.postMessage(eventData, '*');
                    } catch (error) {
                        console.log("Error posting user join message:", error);
                    }
                });

                ZoomMtg.inMeetingServiceListener('onVbStatusChange', function (data) {
                    console.log("--------------- Virtual Background Status Changed ---------------", data);
                    try {
                        changeBG(data);
                    } catch (error) {
                        console.log("Error changing background:", error);
                    }
                });

                ZoomMtg.inMeetingServiceListener('onMeetingStatus', function (data) {
                    console.log("--------------- Meeting Status Changed ---------------", data);
                    if (data?.meetingStatus === 2) {
                        setTimeout(() => {
                            ZoomMtg.mirrorVideo({
                                mirrored: false,
                                success: function () { console.log('✅ Mirror disabled on meeting connect'); },
                                error: function (error) { console.error('❌ Mirror error:', error); }
                            });
                        }, 500);
                    }
                });

                ZoomMtg.inMeetingServiceListener('onUserVideoStatusChange', function (data) {
                    console.log("--------------- User Video Status Changed ---------------", data);
                });
            },
            error: (error) => {
                console.error("--------------- Error in Zoom Init ---------------", error);
                setBgStatus({ isApplied: false, message: `Init error: ${error.reason || 'Unknown error'}`, currentBG: null });
            }
        });
    }

    function applyVirtualBackgrounds(formattedBGs, defaultBG) {
        setBgStatus({ isApplied: false, message: 'Uploading backgrounds...', currentBG: null });

        return new Promise((resolve, reject) => {
            ZoomMtg.updateVirtualBackgroundList({
                vbList: formattedBGs,
                success: function (result) {
                    setBgStatus({ isApplied: false, message: 'Backgrounds uploaded, applying default...', currentBG: null });

                    let backgroundToApply = null;

                    // If we have a default background from the database, find its formatted version
                    if (defaultBG) {
                        const defaultId = String(getBackgroundId(defaultBG));
                        backgroundToApply = formattedBGs.find(bg => String(bg.id) === defaultId);
                    }

                    if (backgroundToApply) {
                        // Apply background immediately without delay
                        ZoomMtg.setVirtualBackground({
                            id: backgroundToApply.id,
                            success: function () {

                                // Store the applied background
                                lastKnownBGRef.current = backgroundToApply;

                                setBgStatus({
                                    isApplied: true,
                                    message: '✅ Virtual background applied successfully!',
                                    currentBG: backgroundToApply
                                });

                                try {
                                    const eventData = JSON.stringify({
                                        eventType: "growkarma_virtualBackgroundApplied",
                                        backgroundId: backgroundToApply.id,
                                        backgroundUrl: backgroundToApply.url,
                                        isLastUsed: !!defaultBG
                                    });
                                    window.top.postMessage(eventData, '*');
                                } catch (error) {
                                    console.error("Error posting background applied message:", error);
                                }

                                // NOW start the video after background is applied
                                setTimeout(() => {
                                    controlVideoState(true);
                                }, 500);

                                resolve();
                            },
                            error: function (error) {
                                console.error('❌ Error setting virtual background:', error);
                                setBgStatus({
                                    isApplied: false,
                                    message: `❌ Failed to apply background: ${error.reason || 'Unknown error'}`,
                                    currentBG: null
                                });
                                reject(error);
                            }
                        });
                    } else {
                        setBgStatus({
                            isApplied: false,
                            message: '⚠️ No background available to apply',
                            currentBG: null
                        });
                        reject(new Error('No background available'));
                    }

                },
                error: function (error) {
                    console.error('❌ Error updating virtual background list:', error);
                    setBgStatus({
                        isApplied: false,
                        message: `❌ Failed to upload backgrounds: ${error.reason || 'Unknown error'}`,
                        currentBG: null
                    });
                    reject(error);
                }
            });
        });
    }

    const changeBG = (data) => {
        // Handle background changes
        if (data) {
            const changedId = data?.id || data?.vbId || data?.backgroundId;
            const changedBG = formattedBGs?.find(bg => String(bg.id) === String(changedId));

            if (changedBG) {
                lastKnownBGRef.current = changedBG;
                setBgStatus({
                    isApplied: true,
                    message: '✅ Background changed!',
                    currentBG: changedBG
                });

                try {
                    const eventData = JSON.stringify({
                        eventType: "growkarma_virtualBackgroundChanged",
                        backgroundId: changedBG.id,
                        userId: initData?.userId
                    });
                    window.top.postMessage(eventData, '*');
                } catch (error) {
                    console.error("Error posting background changed message:", error);
                }
            } else {
                console.warn("⚠️ Could not find changed background in virtualBGs array");
            }
        } else {
            console.warn("⚠️ No id in background change data:", data);
        }
    }

    return (
        <div className="App">
            <main>
                <p>{bgStatus?.message}</p>
            </main>
        </div>
    );
}

export default ZoomMeeting;